import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Seo } from "../components/Seo";
import { Building2, LogIn, AlertCircle } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const redirectTo = location.state?.from || "/admin";

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(redirectTo, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail, e2.message || "Login failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_520px] bg-white">
      <Seo
        title="Admin Login"
        description="Sign in to the BankDesks.com admin console."
        path="/admin/login"
        noindex
      />

      {/* Left hero strip (hidden on small screens) */}
      <div className="hidden lg:block border-r border-[#0A0A0A] bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
              <Building2 className="w-5 h-5" strokeWidth={2.25} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em]">
              BankDesks.com<br /><span className="text-white/60">admin console</span>
            </div>
          </Link>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-3">
              Section 01 / Identity
            </div>
            <h1 className="font-display font-black text-5xl tracking-tighter leading-[1.02]">
              Sign in to manage<br />
              <span className="text-[#4B9CFA]">9,381 institutions.</span>
            </h1>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">
              Admin controls for data enrichment, backups and user management.
              Restricted to authorised operators.
            </p>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
            v1.5 · internal tool — not for public use
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-16 lg:px-12">
        <div className="w-full max-w-sm">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] mb-2">
            Admin / Login
          </div>
          <h2 className="font-display font-black text-4xl tracking-tighter mb-8">
            Authenticate
          </h2>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="email" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                autoFocus
                data-testid="admin-login-email"
                className="w-full border border-[#0A0A0A] px-4 py-3 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
            </div>
            <div>
              <label htmlFor="password" className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563] block mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="admin-login-password"
                className="w-full border border-[#0A0A0A] px-4 py-3 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              />
            </div>

            {err && (
              <div
                data-testid="admin-login-error"
                className="flex items-start gap-2 border-l-4 border-[#FF3333] bg-red-50 px-3 py-2 text-xs text-red-900"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full bg-[#0A0A0A] text-white font-bold px-4 py-3 text-sm tracking-wide flex items-center justify-center gap-2 hover:bg-[#002FA7] transition-colors disabled:opacity-60"
            >
              <LogIn className="w-4 h-4" />
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#E5E7EB] text-xs text-[#4B5563]">
            Don't have access?{" "}
            <Link to="/" className="text-[#002FA7] underline" data-testid="admin-login-back">
              Return to public directory
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
